import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { VeraWordmark } from '../icons/VeraWordmark';
import {
  Home,
  UserCheck,
  Eye,
  MapPin,
  Upload,
  Film,
  Briefcase,
  Clock,
  Users,
  FileText,
  Settings,
  User,
  LogOut,
  Menu,
} from 'lucide-react';

type NavAction = 'signout';

interface NavItem {
  key: string;
  label: string;
  icon: React.ElementType;
  path?: string;
  action?: NavAction;
}

interface RoleNav {
  main: NavItem[];
  bottom: NavItem[];
}

// Shared item definitions
const ITEM = {
  dashboard: { key: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard' },
  clients: { key: 'clients', label: 'Clients', icon: UserCheck, path: '/clients' },
  reviewNotes: { key: 'review-notes', label: 'Review Notes', icon: Eye, path: '/review-notes' },
  locationAudit: { key: 'location-audit', label: 'Location Audit', icon: MapPin, path: '/location-audit' },
  upload: { key: 'upload', label: 'Upload', icon: Upload, path: '/upload' },
  footage: { key: 'footage', label: 'Footage', icon: Film, path: '/footage' },
  hrDashboard: { key: 'hr-dashboard', label: 'HR Dashboard', icon: Briefcase, path: '/hr-dashboard' },
  timesheets: { key: 'timesheets', label: 'Timesheets', icon: Clock, path: '/timesheets' },
  users: { key: 'users', label: 'Users', icon: Users, path: '/users' },
  mySessions: { key: 'my-sessions', label: 'My Sessions', icon: FileText, path: '/my-sessions' },
  settings: { key: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
  profile: { key: 'profile', label: 'Profile', icon: User, path: '/profile' },
  signOut: { key: 'sign-out', label: 'Sign Out', icon: LogOut, action: 'signout' as NavAction },
} satisfies Record<string, NavItem>;

// Role-based navigation. Items in `bottom` are pinned to the bottom of the sidebar.
const NAV_BY_ROLE: Record<string, RoleNav> = {
  admin: {
    main: [
      ITEM.dashboard, ITEM.clients, ITEM.reviewNotes, ITEM.locationAudit,
      ITEM.hrDashboard, ITEM.timesheets,
      ITEM.upload, ITEM.footage, ITEM.users,
    ],
    bottom: [ITEM.settings, ITEM.profile, ITEM.signOut],
  },
  supervisor: {
    main: [
      ITEM.dashboard, ITEM.clients, ITEM.reviewNotes, ITEM.locationAudit,
      ITEM.timesheets, ITEM.upload, ITEM.footage,
    ],
    bottom: [ITEM.profile, ITEM.signOut],
  },
  rbt: {
    main: [ITEM.dashboard, ITEM.mySessions, ITEM.timesheets, ITEM.upload, ITEM.footage],
    bottom: [ITEM.profile, ITEM.signOut],
  },
  viewer: {
    main: [ITEM.dashboard, ITEM.footage],
    bottom: [ITEM.profile, ITEM.signOut],
  },
  // HR is not in the spec; sensible back-office default.
  hr: {
    main: [ITEM.dashboard, ITEM.hrDashboard, ITEM.timesheets, ITEM.users],
    bottom: [ITEM.profile, ITEM.signOut],
  },
};

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggleCollapse: () => void;
  onOpenMobile: () => void;
  onCloseMobile: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  mobileOpen,
  onToggleCollapse,
  onOpenMobile,
  onCloseMobile,
}) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const nav = NAV_BY_ROLE[user?.role || 'viewer'] || NAV_BY_ROLE.viewer;

  const isActive = (path?: string) => !!path && location.pathname === path;

  const handleItem = async (item: NavItem) => {
    if (item.action === 'signout') {
      await signOut();
      navigate('/login');
      return;
    }
    if (item.path) navigate(item.path);
  };

  // The in-sidebar toggle collapses on desktop, but closes the drawer on mobile.
  const handleToggle = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches) {
      onCloseMobile();
    } else {
      onToggleCollapse();
    }
  };

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <button
        key={item.key}
        type="button"
        className={`sidebar-item ${active ? 'active' : ''}`}
        onClick={() => handleItem(item)}
        title={collapsed ? item.label : undefined}
        aria-current={active ? 'page' : undefined}
      >
        <Icon className="nav-icon" size={20} aria-hidden="true" />
        <span className="nav-label">{item.label}</span>
      </button>
    );
  };

  const sidebarClasses = [
    'sidebar',
    collapsed ? 'collapsed' : '',
    mobileOpen ? 'mobile-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Mobile top bar — only visible on small screens; opens the drawer */}
      <div className="sidebar-mobile-topbar">
        <button
          type="button"
          className="sidebar-mobile-toggle"
          onClick={onOpenMobile}
          aria-label="Open navigation menu"
        >
          <Menu size={22} />
        </button>
        <VeraWordmark size={20} color="var(--sidebar-wordmark)" />
      </div>

      <aside className={sidebarClasses} aria-label="Primary navigation">
        {/* Top: brand + collapse toggle */}
        <div className="sidebar-top">
          <div
            className="sidebar-brand"
            onClick={() => navigate('/dashboard')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard'); }}
          >
            <span className="sidebar-brand-full">
              <VeraWordmark size={24} color="var(--sidebar-wordmark)" />
            </span>
            <span className="sidebar-brand-mini" aria-hidden="true">V</span>
          </div>
          <button
            type="button"
            className="sidebar-toggle"
            onClick={handleToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Main nav items */}
        <nav className="sidebar-nav">
          {nav.main.map(renderItem)}
        </nav>

        {/* Divider + pinned bottom items */}
        <div className="sidebar-bottom">
          <div className="sidebar-divider" role="separator" />
          {nav.bottom.map(renderItem)}
        </div>
      </aside>

      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={onCloseMobile} aria-hidden="true" />
      )}
    </>
  );
};
